import React, { useRef, useState } from "react";
import axios from "axios";
import { Search, MapPin, Loader2 } from "lucide-react";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

// "Check ETA for an Address" (plan.md #3) — reuses the same public geocode
// autocomplete the Customer Portal uses, then asks the admin-only
// /routes/address-eta endpoint for the historical average.
export function AddressEtaSearch({ onLocate }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const debounceRef = useRef(null);

  const search = (text) => {
    setResult(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text || text.trim().length < 3) { setSuggestions([]); onLocate?.(null); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/customer/geocode/autocomplete`, { params: { text } });
        setSuggestions(data.results || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const checkEta = async (s) => {
    setOpen(false);
    setQuery(s.label || s.address || "");
    setSuggestions([]);
    if (!s.latitude || !s.longitude) return;
    onLocate?.({ lat: s.latitude, lng: s.longitude, label: s.label || s.address });
    setLoading(true);
    try {
      const { data } = await apiClient.get("/routes/address-eta", { params: { lat: s.latitude, lng: s.longitude } });
      setResult({ label: s.label || s.address, ...data });
    } catch (error) {
      setResult({ label: s.label || s.address, error: getApiErrorMessage(error, "Could not check this address.") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative mb-4 max-w-md" data-testid="address-eta-search">
      <div className="relative">
        <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          className="w-full rounded-md border border-border bg-surface py-2 pl-9 pr-8 text-sm text-text-main outline-none focus:ring-1 focus:ring-primary"
          placeholder="Check delivery ETA for an address..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); search(e.target.value); }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          autoComplete="off"
          data-testid="address-eta-input"
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-text-muted" />}
      </div>
      {open && (suggestions.length > 0 ? (
        <div className="absolute z-[500] mt-1 w-full rounded-md border border-border bg-surface shadow-lg" data-testid="address-eta-suggestions">
          {suggestions.map((s, idx) => (
            <button
              key={idx} type="button" onClick={() => checkEta(s)}
              className="flex w-full items-start gap-2 border-b border-border/60 px-3 py-2 text-left text-sm text-text-main last:border-0 hover:bg-background"
              data-testid={`address-eta-suggestion-${idx}`}
            >
              <MapPin size={14} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-text-muted" />
              <span>{s.label || s.address}</span>
            </button>
          ))}
        </div>
      ) : !loading && query.trim().length >= 3 && (
        <div className="absolute z-[500] mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-muted shadow-lg" data-testid="address-eta-no-suggestions">
          No matching address found.
        </div>
      ))}
      {result && (
        <div className="mt-2 rounded-md border border-border bg-surface p-3 text-sm" data-testid="address-eta-result">
          <p className="font-medium text-text-main">{result.label}</p>
          {result.error ? (
            <p className="mt-1 text-danger" data-testid="address-eta-error">{result.error}</p>
          ) : result.has_data ? (
            <p className="mt-1 text-text-muted" data-testid="address-eta-range">
              In your <span className="font-semibold text-text-main">{result.radius_label}</span> area, delivery timing is typically{" "}
              <span className="font-semibold text-primary">{result.eta_range}</span>{" "}
              ({result.sample_count} past deliveries at the nearest stop, ~{result.nearest_distance_m}m away)
            </p>
          ) : (
            <p className="mt-1 text-text-muted" data-testid="address-eta-no-data">No nearby delivery stop with enough history within 1 km of this address yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
